import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from '@infra/api/query-client.ts';
import { createApiService } from '@infra/api/api-service.ts';
import { API_ENDPOINTS } from '@infra/api/endpoints.ts';
import { router } from './app/router.tsx';
import { WidgetErrorBoundary } from '@features/widget/components/WidgetErrorBoundary.tsx';
import { AssistantMount } from './app/AssistantMount.tsx';
import { ToastContainer } from '@shared/components/ToastContainer.tsx';
import { useAuthStore } from '@features/auth/auth.store.ts';
import { useThemeStore } from '@features/theme/theme.store.ts';
import { API_BASE_URL, API_TIMEOUT } from './config.ts';
import './styles/globals.css';

// Restore isAuthenticated from localStorage before first render
useAuthStore.getState().hydrate();

// Apply light/dark theme before first render to avoid flickering
useThemeStore.getState().init();

// Initialize once before any route renders, including /login
createApiService({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT,
  logging: import.meta.env.DEV,
  auth: {
    type: 'bearer',
    refreshEndpoint: `${API_BASE_URL}${API_ENDPOINTS.AUTH.RENEW_TOKEN}`,
    onAuthFailure: () => {
      useAuthStore.getState().logout();
      void router.navigate('/login', { replace: true });
    },
  },
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <WidgetErrorBoundary>
        <RouterProvider router={router} />
      </WidgetErrorBoundary>
      <ToastContainer />
      <AssistantMount />
    </QueryClientProvider>
  </StrictMode>,
);
